import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Building2, Handshake, TrendingUp, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow, Button } from "../components/primitives";
import { Seo } from "../components/Seo";
import { api, IMAGES } from "../lib/api";

const inputCls =
  "w-full bg-transparent border-b border-line py-3 text-ink placeholder:text-ink-soft/50 focus:outline-none focus:border-gold transition-colors";

const ways = [
  { icon: Heart, key: "donor", title: "Make a Contribution", desc: "Directly fund the tools, training and reach that turn this vision into reality." },
  { icon: Building2, key: "business", title: "Business Supporter", desc: "Back an ethical business as a company or brand aligned with our values." },
  { icon: Handshake, key: "partner", title: "Partnership", desc: "Collaborate with us on community initiatives and shared missions." },
  { icon: TrendingUp, key: "investor", title: "Investor Interest", desc: "Register early interest as we prepare for future growth rounds." },
];

const fadeUpInitial = { opacity: 0, y: 20 };
const fadeUpAnimate = { opacity: 1, y: 0 };

export default function Support() {
  const [type, setType] = useState("donor");
  const [form, setForm] = useState({ name: "", email: "", organization: "", amount: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/support", { ...form, interest_type: type });
      toast.success(data.message);
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Support the Vision" description="Glam Wagon is an early-stage startup. Support us as a donor, business, partner or future investor — and help prove values can outperform profit alone." image={IMAGES.group} />

      <section className="pt-40 pb-16 max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-14 items-center" data-testid="support-hero">
        <Reveal>
          <Eyebrow>Support the vision</Eyebrow>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-[4.8rem] leading-[0.98] tracking-tight mt-6 text-ink">
            Build something meaningful with us.
          </h1>
          <p className="mt-8 text-lg leading-relaxed text-ink-soft">
            We're an early-stage startup — and this is a rare chance to help shape a company from the
            ground up. Supporters aren't giving to charity. They're investing in a better way of
            doing business.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <img src={IMAGES.group} alt="Community" className="rounded-3xl w-full h-[440px] object-cover shadow-xl shadow-black/5" />
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="support-ways">
          {ways.map((w) => (
            <button
              key={w.key}
              onClick={() => setType(w.key)}
              data-testid={`support-way-${w.key}`}
              className={`text-left rounded-2xl border p-7 transition-all duration-300 hover:-translate-y-1 ${
                type === w.key ? "border-gold bg-surface shadow-lg shadow-black/5" : "border-line bg-cream"
              }`}
            >
              <div className={`w-11 h-11 rounded-full flex items-center justify-center ${type === w.key ? "bg-gold text-white" : "bg-surface text-gold"}`}>
                <w.icon size={20} />
              </div>
              <h3 className="font-serif text-xl mt-5 text-ink">{w.title}</h3>
              <p className="mt-2 text-sm text-ink-soft leading-relaxed">{w.desc}</p>
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-12 py-16" data-testid="support-form-section">
        {done ? (
          <motion.div initial={fadeUpInitial} animate={fadeUpAnimate} data-testid="support-success" className="rounded-3xl border border-line bg-surface p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-gold text-white flex items-center justify-center mx-auto"><Check size={26} /></div>
            <h2 className="font-serif text-3xl mt-6 text-ink">Thank you for believing.</h2>
            <p className="mt-3 text-ink-soft">We'll follow up personally with next steps. You're helping prove something the world needs to see.</p>
          </motion.div>
        ) : (
          <Reveal>
            <div className="rounded-3xl border border-line bg-surface p-8 lg:p-12">
              <h2 className="font-serif text-3xl text-ink">Tell us how you'd like to help</h2>
              <form onSubmit={submit} className="mt-8 space-y-7" data-testid="support-form">
                <div className="grid sm:grid-cols-2 gap-7">
                  <input required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} data-testid="support-name" />
                  <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} data-testid="support-email" />
                  <input placeholder="Organization (optional)" value={form.organization} onChange={set("organization")} className={inputCls} data-testid="support-org" />
                  {type === "donor" || type === "investor" ? (
                    <input placeholder={type === "donor" ? "Contribution amount (optional)" : "Intended amount (optional)"} value={form.amount} onChange={set("amount")} className={inputCls} data-testid="support-amount" />
                  ) : <div className="hidden sm:block" />}
                </div>
                <textarea rows={3} placeholder="Your message (optional)" value={form.message} onChange={set("message")} className={inputCls} data-testid="support-message" />
                <Button type="submit" disabled={loading} data-testid="support-submit">
                  {loading ? "Sending…" : type === "donor" ? "Contribute to the vision" : "Register interest"}
                </Button>
                <p className="text-sm text-ink-soft">Payment processing is coming soon — for now we'll reach out with secure contribution details.</p>
              </form>
            </div>
          </Reveal>
        )}
      </section>
    </>
  );
}
